import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, resolveUrl } from "@/lib/queryClient";
import { useLocation } from "wouter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useCustomer } from "@/hooks/use-customer";
import { useAuth } from "@/hooks/use-auth";
import { Loader2, Plus, Trash2, Download, Printer, ArrowLeft } from "lucide-react";

export default function NyserdaPage() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const [isAdding, setIsAdding] = useState(false);
  const { activeCustomerId, setActiveCustomerId } = useCustomer();

  const queryKey = activeCustomerId ? `/api/nyserda?customerId=${activeCustomerId}` : "/api/nyserda";

  const { data: entries, isLoading } = useQuery<any[]>({
    queryKey: [queryKey],
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/nyserda/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast({ title: "Entry deleted" });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const payload = activeCustomerId ? { ...data, customerId: activeCustomerId } : data;
      await apiRequest("POST", "/api/nyserda", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setIsAdding(false);
      toast({ title: "Entry added" });
    },
  });

  const handleExport = () => {
    try {
      const queryParams = new URLSearchParams(reportConfigs as any);
      if (activeCustomerId) {
        queryParams.append("customerId", activeCustomerId);
      }
      
      // Use direct window location change for native browser download instead of fetch+blob
      const exportUrl = resolveUrl(`/api/nyserda/export/excel?${queryParams.toString()}`);
      
      window.location.assign(exportUrl);
      
      toast({ title: "Export Started", description: "Your NYSERDA form is downloading." });
    } catch (error) {
      console.error("Export error:", error);
      toast({ title: "Export Failed", description: "Could not initiate export.", variant: "destructive" });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const [newEntry, setNewEntry] = useState({
    measureName: "",
    measureDescription: "",
    status: "Identified",
    implementationDate: "TBD",
    totalProjectCost: 0,
    incentive: 0,
    electricSavings: 0,
    gasSavings: 0,
    energyCostSavings: 0,
    simplePayback: 0
  });

  const [reportConfigs, setReportConfigs] = useState({
    projectName: "OOE Facility Retrofit",
    reportingPeriod: "Q4",
    osemProjectNumber: "PO-ROTB-QUEUE",
    osemStartDate: new Date().toLocaleDateString(),
    osemName: "Capital Planning Pipeline",
    electricSavingsTarget: "15%",
    osemCompany: "Facility Owner",
    fossilFuelTarget: "0%",
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const inputClass = "h-7 px-1 font-medium border-0 border-b border-dashed border-slate-300 rounded-none bg-transparent hover:bg-slate-100 focus-visible:ring-0 focus-visible:border-primary w-full";

  return (
    <div className="container mx-auto py-8">
      {/* Hide controls when printing */}
      <div className="flex justify-between items-center mb-8 print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setLocation('/')} className="mb-1 p-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              ECM Summary
            </h1>
            <p className="text-muted-foreground">
              Active Measures Pipeline
            </p>
          </div>
        </div>
        <div className="flex gap-4">
          <Button onClick={() => setIsAdding(!isAdding)} variant="outline">
            <Plus className="mr-2 h-4 w-4" />
            Add Custom Entry
          </Button>
          <Button onClick={handlePrint} variant="outline">
            <Printer className="mr-2 h-4 w-4" />
            Print Form
          </Button>
          <Button onClick={handleExport} className="bg-green-600 hover:bg-green-700">
            <Download className="mr-2 h-4 w-4" />
            Export to Excel
          </Button>
        </div>
      </div>

      {isAdding && (
        <Card className="mb-8 print:hidden">
          <CardHeader>
            <CardTitle>Add Custom Entry</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate(newEntry);
            }} className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Measure Name</Label>
                <Input value={newEntry.measureName} onChange={e => setNewEntry({...newEntry, measureName: e.target.value})} required />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Description</Label>
                <Input value={newEntry.measureDescription} onChange={e => setNewEntry({...newEntry, measureDescription: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Project Cost ($)</Label>
                <Input type="number" value={newEntry.totalProjectCost || ""} onChange={e => {
                  const val = parseFloat(e.target.value) || 0;
                  const payback = newEntry.energyCostSavings > 0 ? parseFloat((val / newEntry.energyCostSavings).toFixed(1)) : 0;
                  setNewEntry({...newEntry, totalProjectCost: val, simplePayback: payback});
                }} />
              </div>
              <div className="space-y-2">
                <Label>Incentive ($)</Label>
                <Input type="number" value={newEntry.incentive || ""} onChange={e => setNewEntry({...newEntry, incentive: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Electric Savings (kWh)</Label>
                <Input type="number" value={newEntry.electricSavings || ""} onChange={e => setNewEntry({...newEntry, electricSavings: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Gas Savings (MMBtu)</Label>
                <Input type="number" value={newEntry.gasSavings || ""} onChange={e => setNewEntry({...newEntry, gasSavings: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Cost Savings ($/yr)</Label>
                <Input type="number" value={newEntry.energyCostSavings || ""} onChange={e => {
                  const val = parseFloat(e.target.value) || 0;
                  const payback = val > 0 ? parseFloat((newEntry.totalProjectCost / val).toFixed(1)) : 0;
                  setNewEntry({...newEntry, energyCostSavings: val, simplePayback: payback});
                }} />
              </div>
              <div className="space-y-2">
                <Label>Simple Payback (yrs)</Label>
                <Input type="number" step="0.1" value={newEntry.simplePayback} readOnly className="bg-slate-50 cursor-not-allowed" />
              </div>
              
              <div className="col-span-2 md:col-span-4 flex justify-end gap-2 mt-4">
                <Button type="button" variant="ghost" onClick={() => setIsAdding(false)}>Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Entry
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* NYSERDA Printable Form Area */}
      <Card className="border shadow-none">
        <CardHeader className="bg-slate-50 border-b">
          <h2 className="text-xl font-bold uppercase text-center mb-6">On-site Energy Manager Results (Appendix P)</h2>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-4 gap-x-8 text-sm">
            <div className="flex flex-col">
              <span className="font-semibold text-muted-foreground">Project Name</span>
              <Input className={inputClass} value={reportConfigs.projectName} onChange={(e) => setReportConfigs({...reportConfigs, projectName: e.target.value})} />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-muted-foreground">Reporting Period</span>
              <Input className={inputClass} value={reportConfigs.reportingPeriod} onChange={(e) => setReportConfigs({...reportConfigs, reportingPeriod: e.target.value})} />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-muted-foreground">OsEM Project Number</span>
              <Input className={inputClass} value={reportConfigs.osemProjectNumber} onChange={(e) => setReportConfigs({...reportConfigs, osemProjectNumber: e.target.value})} />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-muted-foreground">OsEM Start Date</span>
              <Input className={inputClass} value={reportConfigs.osemStartDate} onChange={(e) => setReportConfigs({...reportConfigs, osemStartDate: e.target.value})} />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-muted-foreground">OsEM Name</span>
              <Input className={inputClass} value={reportConfigs.osemName} onChange={(e) => setReportConfigs({...reportConfigs, osemName: e.target.value})} />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-muted-foreground">Elec. Savings Target (%)</span>
              <Input className={inputClass} value={reportConfigs.electricSavingsTarget} onChange={(e) => setReportConfigs({...reportConfigs, electricSavingsTarget: e.target.value})} />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-muted-foreground">OsEM Company</span>
              <Input className={inputClass} value={reportConfigs.osemCompany} onChange={(e) => setReportConfigs({...reportConfigs, osemCompany: e.target.value})} />
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-muted-foreground">Fossil Fuel Target (%)</span>
              <Input className={inputClass} value={reportConfigs.fossilFuelTarget} onChange={(e) => setReportConfigs({...reportConfigs, fossilFuelTarget: e.target.value})} />
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-0 overflow-x-auto">
          <Table className="text-xs">
            <TableHeader className="bg-slate-100">
              <TableRow>
                <TableHead className="w-[80px] font-bold text-black border-r border-slate-200">ECM#</TableHead>
                <TableHead className="font-bold text-black border-r border-slate-200 min-w-[150px]">Measure Name</TableHead>
                {activeCustomerId === null && (
                  <TableHead className="font-bold text-black border-r border-slate-200 min-w-[120px]">Facility</TableHead>
                )}
                <TableHead className="font-bold text-black border-r border-slate-200 min-w-[200px]">Measure Description</TableHead>
                <TableHead className="font-bold text-black border-r border-slate-200">Status</TableHead>
                <TableHead className="font-bold text-black border-r border-slate-200 text-right">Proj Cost</TableHead>
                <TableHead className="font-bold text-black border-r border-slate-200 text-right">Net Cost</TableHead>
                <TableHead className="font-bold text-black border-r border-slate-200 text-right">Elec (kWh)</TableHead>
                <TableHead className="font-bold text-black border-r border-slate-200 text-right">Gas (MMBtu)</TableHead>
                <TableHead className="font-bold text-black border-r border-slate-200 text-right">Cost Sav.</TableHead>
                <TableHead className="font-bold text-black border-r border-slate-200 text-right">Payback</TableHead>
                <TableHead className="print:hidden w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={activeCustomerId === null ? 12 : 11} className="text-center text-muted-foreground py-8">
                    Queue is empty. Save analyses or add custom entries.
                  </TableCell>
                </TableRow>
              )}
              {entries?.map((entry: any) => {
                const totalEnergySavings = (entry.electricSavings * 0.003412) + (entry.gasSavings || 0);
                const netCost = (entry.totalProjectCost || 0) - (entry.incentive || 0);

                return (
                  <TableRow key={entry.id} className="hover:bg-slate-50 border-b border-slate-200">
                    <TableCell className="font-medium border-r border-slate-100">{entry.ecmNumber || `Custom-${entry.id}`}</TableCell>
                    <TableCell className="border-r border-slate-100">{entry.measureName}</TableCell>
                    {activeCustomerId === null && (
                      <TableCell className="border-r border-slate-100">
                        <span 
                          className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary cursor-pointer hover:bg-primary/20"
                          onClick={() => {
                            if (entry.buildingId) {
                              setActiveCustomerId(entry.buildingId);
                            }
                          }}
                        >
                          {entry.buildingName || "Unknown"}
                        </span>
                      </TableCell>
                    )}
                    <TableCell className="border-r border-slate-100 text-muted-foreground">{entry.measureDescription}</TableCell>
                    <TableCell className="border-r border-slate-100">{entry.status}</TableCell>
                    <TableCell className="text-right border-r border-slate-100">${(entry.totalProjectCost || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right border-r border-slate-100">${netCost.toLocaleString()}</TableCell>
                    <TableCell className="text-right border-r border-slate-100">{(entry.electricSavings || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right border-r border-slate-100">{(entry.gasSavings || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right border-r border-slate-100">${(entry.energyCostSavings || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right border-r border-slate-100 bg-slate-50 font-medium">
                      {entry.simplePayback || (entry.energyCostSavings > 0 ? parseFloat((entry.totalProjectCost / entry.energyCostSavings).toFixed(1)) : 0) || '-'}
                    </TableCell>
                    <TableCell className="print:hidden text-center">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => deleteMutation.mutate(entry.id)}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              
              {/* Totals Row */}
              {entries && entries.length > 0 && (
                <TableRow className="bg-slate-100 font-bold text-black">
                  <TableCell colSpan={activeCustomerId === null ? 5 : 4} className="text-right border-r border-slate-200">TOTALS:</TableCell>
                  <TableCell className="text-right border-r border-slate-200">
                    ${entries.reduce((sum: number, e: any) => sum + (e.totalProjectCost || 0), 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right border-r border-slate-200">
                    ${entries.reduce((sum: number, e: any) => sum + ((e.totalProjectCost || 0) - (e.incentive || 0)), 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right border-r border-slate-200">
                    {entries.reduce((sum: number, e: any) => sum + (e.electricSavings || 0), 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right border-r border-slate-200">
                    {entries.reduce((sum: number, e: any) => sum + (e.gasSavings || 0), 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right border-r border-slate-200">
                    ${entries.reduce((sum: number, e: any) => sum + (e.energyCostSavings || 0), 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right border-r border-slate-200">
                    {/* Average simple payback approx */}
                    {(
                      entries.reduce((sum: number, e: any) => sum + ((e.totalProjectCost || 0) - (e.incentive || 0)), 0) / 
                      (entries.reduce((sum: number, e: any) => sum + (e.energyCostSavings || 0), 0) || 1)
                    ).toFixed(1)}
                  </TableCell>
                  <TableCell className="print:hidden"></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
